import Text, { TextOverrideProps } from "@src/lib/ui/Text";
import NextLink from "next/link";
import { Route } from "nextjs-routes";
import { CSSProperties, FC, ReactNode, useMemo, useState } from "react";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import { cn } from "@src/lib/utils/cn";
import { INLINE_EDIT_ROW_INTERACTIVE_CLASSNAME } from "@src/lib/ui/inlineEditStyles";

export type Link = { text: string; url: Route };

export type InlineEditButtonProps = {
  values: string[] | Link[];
  onClick?: () => void;
  loading?: boolean;
  style?: CSSProperties;
  className?: string;
  placeholder?: string;
  iconType?: ReactNode;
  alwaysShowIcon?: boolean;
  sortValues?: boolean;
};

const InlineEditButton: FC<InlineEditButtonProps> = ({
  values,
  onClick,
  loading,
  style,
  className,
  placeholder = "Empty",
  iconType = <PenOutlineIcon className="size-4" />,
  alwaysShowIcon = false,
  sortValues = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const areValuesLinks = useMemo(() => {
    // An empty list is plain text (the placeholder), not links.
    return values.length > 0 && typeof values[0] !== "string";
  }, [values]);
  const sortedValues = useMemo(() => {
    if (sortValues) {
      // Copy first: sort() in place would reorder the caller's array.
      return [...values].sort() as typeof values;
    } else {
      return values;
    }
  }, [sortValues, values]);

  // Plain-text rows are one keyboard target; link rows keep their links
  // tabbable and the pencil stays pointer-only.
  const isKeyboardTarget = !!onClick && !loading && !areValuesLinks;

  return (
    <div
      className={cn(
        "relative flex items-center rounded-lg bg-transparent min-h-8",
        onClick && !loading && INLINE_EDIT_ROW_INTERACTIVE_CLASSNAME,
        className,
      )}
      style={style}
      role={isKeyboardTarget ? "button" : undefined}
      tabIndex={isKeyboardTarget ? 0 : undefined}
      onKeyDown={
        isKeyboardTarget
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                // The editors listen for Enter on window to save; without
                // this the same keypress would open and then save + close.
                event.stopPropagation();
                onClick?.();
              }
            }
          : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="flex flex-wrap items-center py-0.5 pl-2"
        onClick={() => {
          if (onClick && !loading && !areValuesLinks) onClick();
        }}
      >
        {loading && (
          <Text variant="compact-title" multiline loading={true}>
            {""}
          </Text>
        )}
        {sortedValues.length === 0 && !loading && (
          <Text
            variant="compact-description"
            multiline
            className="text-text-tertiary"
          >
            {placeholder}
          </Text>
        )}
        {!loading &&
          sortedValues.map((value, index) => {
            if (typeof value === "string") {
              return (
                <span key={`text-${index}-${value}`} className="flex">
                  <Text variant="compact-description" multiline>
                    {value}
                  </Text>
                  {index !== values.length - 1 && (
                    <span className="w-2.5">,</span>
                  )}
                </span>
              );
            } else {
              return (
                <>
                  <span key={`text-${index}-${value}`} className="flex">
                    <NextLink
                      href={value.url}
                      target="_blank"
                      className="text-[unset] underline"
                    >
                      <Text variant="compact-description" multiline>
                        {value.text}
                      </Text>
                    </NextLink>
                  </span>
                  <span className="w-2.5">
                    {index !== values.length - 1 && ","}
                  </span>
                </>
              );
            }
          })}
      </div>
      <div
        className="flex flex-1 items-start justify-end self-stretch py-1.5 pr-1.5"
        onClick={() => {
          if (onClick) onClick();
        }}
      >
        <span
          className={cn(
            "flex size-4 items-center justify-center",
            (isHovered || alwaysShowIcon) && onClick
              ? "text-icon-secondary"
              : "text-transparent",
          )}
        >
          {iconType}
        </span>
      </div>
    </div>
  );
};

export type TooltipPosition = "Top" | "Right" | "Bottom" | "Left";

type InlineEditErrorProps = {
  text: string;
  textProps?: TextOverrideProps;
  caretPosition?: TooltipPosition;
  variant?: "default" | "error";
  maxWidth?: number;
  multiline?: boolean;
  containerStyle?: CSSProperties;
  containerClassName?: string;
  tooltipStyle?: CSSProperties;
  tooltipClassName?: string;
};

export const InlineEditError: FC<InlineEditErrorProps> = ({
  text,
  textProps,
  variant = "default",
  caretPosition,
  maxWidth = 0,
  multiline = false,
  containerStyle,
  containerClassName,
  tooltipStyle,
  tooltipClassName,
}) => {
  return (
    <div
      className={cn(
        "flex items-center",
        caretPosition === "Top" && "flex-col-reverse",
        caretPosition === "Right" && "flex-row",
        caretPosition === "Bottom" && "flex-col",
        caretPosition === "Left" && "flex-row-reverse",
        containerClassName,
      )}
      style={containerStyle}
    >
      <div
        className={cn(
          "p-3 rounded-lg",
          maxWidth === 0 ? "flex items-center" : "block",
          variant === "error" ? "bg-status-error-solid" : "bg-text-primary",
          tooltipClassName,
        )}
        style={{
          width: maxWidth === 0 ? "auto" : maxWidth,
          ...tooltipStyle,
        }}
      >
        {/* Legacy typography: callers override via TextOverrideProps (type/fontWeight). */}
        <Text
          type="P2"
          fontWeight="Medium"
          className={
            variant === "error"
              ? "text-action-destructive-foreground"
              : "text-text-inverse"
          }
          multiline={multiline}
          {...textProps}
        >
          {text}
        </Text>
      </div>
      <div
        className={cn(
          "w-0 h-0",
          caretPosition === "Top" &&
            "border-r-[6px] border-r-transparent border-b-[6px] border-b-solid border-l-[6px] border-l-transparent",
          caretPosition === "Right" &&
            "border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-solid",
          caretPosition === "Bottom" &&
            "border-t-[6px] border-t-solid border-r-[6px] border-r-transparent border-l-[6px] border-l-transparent",
          caretPosition === "Left" &&
            "border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-solid",
          caretPosition === "Bottom" &&
            (variant === "error"
              ? "border-t-status-error-solid"
              : "border-t-text-primary"),
          caretPosition === "Top" &&
            (variant === "error"
              ? "border-b-status-error-solid"
              : "border-b-text-primary"),
          caretPosition === "Left" &&
            (variant === "error"
              ? "border-r-status-error-solid"
              : "border-r-text-primary"),
          caretPosition === "Right" &&
            (variant === "error"
              ? "border-l-status-error-solid"
              : "border-l-text-primary"),
          tooltipClassName,
        )}
        style={tooltipStyle}
      ></div>
    </div>
  );
};

export default InlineEditButton;
